import type { ChatAttachment, ChatItem } from './types';
import { HTML_ENTITY_TEXT_TOKEN } from './htmlText';

/** Domain-specific link preview height estimator in px for a content width in px. */
export type PreviewHeightEstimator = (
  preview: ChatItem['previews'][number],
  contentWidth: number
) => number;

/** Domain-specific attachment block height estimator in px for a content width in px. */
export type AttachmentsHeightEstimator = (
  attachments: readonly ChatAttachment[],
  contentWidth: number,
  item?: ChatItem
) => number;

/** Resolves the rendered text box width without changing attachment or preview geometry. */
export type TextContentWidthResolver = (item: ChatItem, contentWidth: number) => number;

/** Tunable px constants for deterministic chat row height estimation. */
export interface HeightEstimateConfig {
  readonly avgCharW: number;
  readonly lineH: number;
  readonly bubblePaddingY: number;
  readonly authorChromeH: number;
  readonly dateDividerH: number;
  readonly deletedStubH: number;
  readonly reactionRowH: number;
  readonly resolveTextContentWidth?: TextContentWidthResolver;
  readonly attachmentGap: number;
  readonly fileCardH: number;
  readonly maxMediaW: number;
  readonly maxMediaH: number;
  readonly estimateAttachmentsHeight?: AttachmentsHeightEstimator;
  /** Overrides emoji-only (jumbo) row estimation; receives glyph count and content width. */
  readonly estimateEmojiOnlyHeight?: (count: number, contentWidth: number) => number;
  readonly estimatePreviewHeight?: PreviewHeightEstimator;
  readonly previewBlockPaddingTop: number;
  readonly previewGap: number;
  readonly previewH: number;
  readonly minRowH: number;
  readonly jumboEmojiHByCount: readonly [one: number, two: number, three: number, fourOrMore: number];
}

/** Default estimator config calibrated for the current feed row rendering. */
export const DEFAULT_HEIGHT_ESTIMATE_CONFIG: HeightEstimateConfig = {
  avgCharW: 7.2,
  lineH: 20,
  bubblePaddingY: 16,
  authorChromeH: 22,
  dateDividerH: 38,
  deletedStubH: 34,
  reactionRowH: 28,
  attachmentGap: 6,
  fileCardH: 64,
  maxMediaW: 540,
  maxMediaH: 360,
  previewBlockPaddingTop: 0,
  previewGap: 0,
  previewH: 96,
  minRowH: 40,
  jumboEmojiHByCount: [52, 56, 60, 64]
};

/** Estimates a full chat row height in px; throws on invalid content width or media dimensions. */
export function estimateItemHeight(
  item: ChatItem,
  contentWidth: number,
  config = DEFAULT_HEIGHT_ESTIMATE_CONFIG
): number {
  assertFinitePositive(contentWidth, 'contentWidth');

  let height = 0;
  if (item.dateDivider) height += config.dateDividerH;
  if (item.deleted) return height + config.deletedStubH;
  if (!item.groupedWithPrev) height += config.authorChromeH;

  if (!item.containsOnlyAttachments && item.containsOnlyEmojiEntities === true && item.body.length > 0) {
    height += estimateJumboEmojiHeight(item.body, config, contentWidth);
  } else if (!item.containsOnlyAttachments && item.body.length > 0) {
    const textContentWidth = config.resolveTextContentWidth?.(item, contentWidth) ?? contentWidth;
    height += estimateTextHeight(item.body, textContentWidth, config);
  }
  height += estimateAttachmentBlockHeight(item.attachments, contentWidth, config, item);
  height += estimatePreviewBlockHeight(item.previews, contentWidth, config);
  if (item.reactions.length > 0) height += config.reactionRowH;

  return Math.max(config.minRowH, height);
}

/** Estimates text bubble height in px from character count and content width. */
export function estimateTextHeight(
  text: string,
  contentWidth: number,
  config = DEFAULT_HEIGHT_ESTIMATE_CONFIG
): number {
  assertFinitePositive(contentWidth, 'contentWidth');
  const usableWidth = Math.max(1, contentWidth);
  const lineCount = Math.max(1, Math.ceil((text.length * config.avgCharW) / usableWidth));
  return lineCount * config.lineH + config.bubblePaddingY;
}

/** Estimates jumbo emoji/entity-only height in px using token count buckets. */
export function estimateJumboEmojiHeight(
  text: string,
  config = DEFAULT_HEIGHT_ESTIMATE_CONFIG,
  contentWidth?: number
): number {
  const count = Math.max(1, Math.ceil(text.length / HTML_ENTITY_TEXT_TOKEN.length));
  if (config.estimateEmojiOnlyHeight && contentWidth !== undefined) {
    return config.estimateEmojiOnlyHeight(count, contentWidth);
  }
  const tableIndex = Math.min(count, config.jumboEmojiHByCount.length) - 1;
  return config.jumboEmojiHByCount[tableIndex];
}

/** Computes displayed media height in px from intrinsic dimensions and content width. */
export function mediaHeight(
  attachment: ChatAttachment,
  contentWidth: number,
  config = DEFAULT_HEIGHT_ESTIMATE_CONFIG
): number {
  if (attachment.width === undefined || attachment.height === undefined) {
    throw new Error('media attachments must have intrinsic dimensions');
  }

  assertFinitePositive(attachment.width, 'attachment.width');
  assertFinitePositive(attachment.height, 'attachment.height');
  assertFinitePositive(contentWidth, 'contentWidth');

  const displayedW = Math.min(contentWidth, config.maxMediaW);
  return Math.min(config.maxMediaH, (displayedW * attachment.height) / attachment.width);
}

/** Estimates a stacked/grid attachment block height in px, including leading gaps. */
export function estimateAttachmentBlockHeight(
  attachments: readonly ChatAttachment[],
  contentWidth: number,
  config = DEFAULT_HEIGHT_ESTIMATE_CONFIG,
  item?: ChatItem
): number {
  const estimateAttachmentsHeight = config.estimateAttachmentsHeight;
  if (estimateAttachmentsHeight) {
    return estimateAttachmentsHeight(attachments, contentWidth, item);
  }

  if (attachments.length === 0) return 0;
  if (attachments.every(isFileAttachment)) {
    return config.attachmentGap + stackedAttachmentHeight(attachments, contentWidth, config);
  }
  if (!attachments.every(isMediaAttachment)) {
    return config.attachmentGap + stackedAttachmentHeight(attachments, contentWidth, config);
  }
  if (attachments.length === 1) return config.attachmentGap + mediaHeight(attachments[0], contentWidth, config);

  const columns = 2;
  const columnW = (Math.min(contentWidth, config.maxMediaW) - config.attachmentGap) / columns;
  const rows: number[] = [];
  for (let index = 0; index < attachments.length; index += columns) {
    const rowAttachments = attachments.slice(index, index + columns);
    rows.push(Math.max(...rowAttachments.map((attachment) => mediaHeight(attachment, columnW, config))));
  }

  return config.attachmentGap + rows.reduce((sum, rowH) => sum + rowH, 0) + (rows.length - 1) * config.attachmentGap;
}

/** Estimates the stacked link-preview block height in px, including optional list spacing. */
export function estimatePreviewBlockHeight(
  previews: ChatItem['previews'],
  contentWidth: number,
  config = DEFAULT_HEIGHT_ESTIMATE_CONFIG
): number {
  if (previews.length === 0) return 0;

  const estimatePreviewHeight = config.estimatePreviewHeight ??
    (() => config.previewH);
  const previewsHeight = previews.reduce(
    (sum, preview) => sum + estimatePreviewHeight(preview, contentWidth),
    0
  );

  return config.previewBlockPaddingTop +
    previewsHeight +
    (previews.length - 1) * config.previewGap;
}

function stackedAttachmentHeight(
  attachments: readonly ChatAttachment[],
  contentWidth: number,
  config: HeightEstimateConfig
): number {
  return attachments.reduce((sum, attachment, index) => {
    const gap = index === 0 ? 0 : config.attachmentGap;
    return sum + gap + attachmentHeight(attachment, contentWidth, config);
  }, 0);
}

function attachmentHeight(
  attachment: ChatAttachment,
  contentWidth: number,
  config: HeightEstimateConfig
): number {
  return isFileAttachment(attachment)
    ? config.fileCardH
    : mediaHeight(attachment, contentWidth, config);
}

function isFileAttachment(attachment: ChatAttachment): boolean {
  return attachment.kind === 'file';
}

function isMediaAttachment(attachment: ChatAttachment): boolean {
  return attachment.kind === 'image' || attachment.kind === 'video';
}

function assertFinitePositive(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number`);
  }
}
